import { useEffect } from "react";
import { z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import {
  AUTOCOMPLETE_REASON,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  INPUT_TYPES,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { toKebabCase } from "@miplatform/utils";
import { MenuItem } from "@mui/material";
import { useWorkOrderTemplatesTabViewModel } from "./useWorkOrderTemplatesTabViewModel";

const initialData = {
  name: "",
};

export function AddWorkOrderTemplateModal({
  orderTemplateId,
  open,
  title,
  onClose,
  loading,
  onSubmit = () => null,
}) {
  const { allWorkOrderTemplates, loadWorkOrderTemplates } =
    useWorkOrderTemplatesTabViewModel({
      orderTemplateId,
    });
  const edit = useEdit(initialData);

  const fields = [
    {
      label: ENTITY_NAMES.WORK_ORDER_TEMPLATE,
      name: "name",
      fieldType: INPUT_TYPES.AUTOCOMPLETE,
      options: allWorkOrderTemplates,
      disableClearable: true,
      renderOption: (props, { value, key }) => (
        <MenuItem
          {...props}
          key={key}
          data-testid={`template-option-${toKebabCase(value)}`}
        >
          {value}
        </MenuItem>
      ),
      fullWidth: true,
      width: "100%",
      required: true,
      validationRule: z
        .string()
        .min(1, `${ENTITY_NAMES.WORK_ORDER_TEMPLATE} is required`),
      handleChange: (event, option) => {
        const value = option?.value || "";
        edit.update({ name: value });
      },
      onInputChange: (event, value, reason) => {
        if (reason === AUTOCOMPLETE_REASON.CLEAR) {
          edit.update({ name: "" });
        } else if (reason === AUTOCOMPLETE_REASON.INPUT) {
          edit.update({ name: value || "" });
        }
      },
    },
  ];

  edit.setValidation(fields);

  const handleModalClose = () => {
    onClose();
    edit.reset();
  };

  const onAddWorkOrderTemplate = () => {
    if (!edit.isValid()) {
      return;
    }

    const workOrderTemplateName = edit.getValue("name");
    if (!workOrderTemplateName) {
      return;
    }
    const workOrderTemplateId = allWorkOrderTemplates.find(
      (template) => template.name === workOrderTemplateName
    )?.id;

    if (workOrderTemplateId) {
      onSubmit({
        dependentId: workOrderTemplateId,
        principalId: orderTemplateId,
        principalType: "Order",
        dependentType: "WorkOrder",
        ...edit.getValue(),
      });
    }

    handleModalClose();
  };

  useEffect(() => {
    loadWorkOrderTemplates();
  }, []);

  return (
    <MiModal
      open={open}
      closeModal={handleModalClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleModalClose,
        },
        {
          name: "Submit",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          color: BUTTON_COLORS.PRIMARY,
          onClick: onAddWorkOrderTemplate,
          disabled: loading || !edit.isValid(),
        },
      ]}
      title={title}
      topRightClose={handleModalClose}
      loading={loading}
    >
      <MiDetailFields detailFields={fields} edit={edit} />
    </MiModal>
  );
}
